import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import useApproachingBuses from '../hooks/useApproachingBuses';
import useRouteSearch from '../hooks/useRouteSearch';
import { trackEvent } from '../utils/analytics';
import './BusRoutes.css';

const BusRoutes = ({ stopNumber }) => {
  const { t } = useTranslation();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchingRoute, setSearchingRoute] = useState(null);
  const [sortedRoutes, setSortedRoutes] = useState([]);
  const initialSortDone = useRef(false);

  // Fetch approaching buses for arrival times
  const { buses, lastUpdated } = useApproachingBuses(stopNumber);

  // Hook for searching buses on previous stops
  const { searchForBus, searchResults, clearSearchResult } = useRouteSearch(stopNumber);

  // Create a map of route code -> nearest bus arrival info
  const arrivalsByRoute = useMemo(() => {
    const map = {};
    buses.forEach(bus => {
      const routeCode = bus.routeNumber;
      if (!routeCode) return;

      // Keep only the nearest bus for each route
      if (!map[routeCode] || parseInt(bus.timeInMinutes) < parseInt(map[routeCode].timeInMinutes)) {
        map[routeCode] = {
          timeInMinutes: bus.timeInMinutes,
          stopsAway: bus.stopsAway,
          fromCurrentStop: true
        };
      }
    });
    return map;
  }, [buses]);

  // Merge direct arrivals with search results
  const combinedArrivals = useMemo(() => {
    const combined = { ...arrivalsByRoute };

    Object.entries(searchResults).forEach(([routeCode, result]) => {
      // Only use search result if no direct arrival
      if (!combined[routeCode]) {
        if (result.found) {
          combined[routeCode] = {
            timeInMinutes: result.timeInMinutes,
            stopsAway: result.stopsAway,
            fromCurrentStop: false,
            estimated: true,
            foundAtStop: result.foundAtStopName
          };
        } else if (result.notStarted) {
          combined[routeCode] = {
            notStarted: true,
            fromCurrentStop: false
          };
        } else if (result.noRouteData) {
          combined[routeCode] = {
            noRouteData: true,
            fromCurrentStop: false
          };
        } else if (result.stopNotInRoute) {
          combined[routeCode] = {
            stopNotInRoute: true,
            fromCurrentStop: false
          };
        } else if (result.rateLimited) {
          combined[routeCode] = {
            rateLimited: true,
            fromCurrentStop: false
          };
        } else if (result.localRateLimited) {
          combined[routeCode] = {
            localRateLimited: true,
            fromCurrentStop: false
          };
        } else if (result.stopsLimited) {
          combined[routeCode] = {
            stopsLimited: true,
            fromCurrentStop: false
          };
        }
      }
    });

    return combined;
  }, [arrivalsByRoute, searchResults]);

  useEffect(() => {
    const fetchRoutes = async () => {
      if (!stopNumber) return;

      try {
        setLoading(true);
        setError(null);
        initialSortDone.current = false;

        const response = await axios.get(`/api/BusStops/routes/${stopNumber}`);
        setRoutes(response.data || []);
      } catch (err) {
        console.error('Error fetching routes:', err);
        setError(err.response?.data?.error || t('busRoutes.error'));
        setRoutes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRoutes();
  }, [stopNumber, t]);

  // Sort routes once when both routes and buses are loaded
  // Sorting: first by arrival time (nearest first), then alphabetically by route name
  useEffect(() => {
    if (routes.length === 0 || initialSortDone.current) return;

    // Wait for buses data (lastUpdated indicates data was fetched)
    // If no buses after first update, sort anyway
    if (!lastUpdated && Object.keys(arrivalsByRoute).length === 0) {
      // Wait a bit for buses to load
      const timer = setTimeout(() => {
        if (!initialSortDone.current) {
          const sorted = [...routes].sort((a, b) => {
            // Sort alphabetically by route name
            return (a.routeName || a.routeCode).localeCompare(b.routeName || b.routeCode, undefined, { numeric: true });
          });
          setSortedRoutes(sorted);
          initialSortDone.current = true;
        }
      }, 1000);
      return () => clearTimeout(timer);
    }

    const sorted = [...routes].sort((a, b) => {
      const arrivalA = arrivalsByRoute[a.routeCode];
      const arrivalB = arrivalsByRoute[b.routeCode];

      // Both have arrival times - sort by time (nearest first)
      if (arrivalA && arrivalB) {
        return parseInt(arrivalA.timeInMinutes) - parseInt(arrivalB.timeInMinutes);
      }

      // Only A has arrival time - A comes first
      if (arrivalA) return -1;

      // Only B has arrival time - B comes first
      if (arrivalB) return 1;

      // Neither has arrival time - sort alphabetically by route name
      return (a.routeName || a.routeCode).localeCompare(b.routeName || b.routeCode, undefined, { numeric: true });
    });

    setSortedRoutes(sorted);
    initialSortDone.current = true;
  }, [routes, arrivalsByRoute, lastUpdated]);

  // Handle click on route card to search for bus
  const handleRouteClick = useCallback(async (routeCode) => {
    const directArrival = arrivalsByRoute[routeCode];

    // If we already have direct arrival data, no need to search
    if (directArrival) return;

    // If already searching this route, skip
    if (searchingRoute === routeCode) return;

    // If we already have search result for this route, clear it and search again
    if (searchResults[routeCode]) {
      clearSearchResult(routeCode);
    }

    setSearchingRoute(routeCode);
    trackEvent('route_search_submit', {
      route_code: routeCode,
      stop_number: stopNumber
    });
    const result = await searchForBus(routeCode);
    const status = result?.found
      ? 'found'
      : result?.notStarted
        ? 'not_started'
        : result?.noRouteData
          ? 'no_route_data'
          : result?.stopNotInRoute
            ? 'stop_not_in_route'
            : result?.rateLimited
              ? 'rate_limited'
              : result?.localRateLimited
                ? 'local_rate_limited'
                : result?.stopsLimited
                  ? 'stops_limited'
                  : result?.error
                    ? 'error'
                    : 'not_found';
    trackEvent('route_search_result', {
      route_code: routeCode,
      stop_number: stopNumber,
      status
    });
    setSearchingRoute(null);
  }, [arrivalsByRoute, searchResults, searchingRoute, searchForBus, clearSearchResult, stopNumber]);

  if (loading) {
    return (
      <div className="bus-routes-loading">
        <div className="spinner"></div>
        <span>{t('busRoutes.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bus-routes-error">
        <span className="error-icon">⚠️</span>
        <span>{error}</span>
      </div>
    );
  }

  if (routes.length === 0 || sortedRoutes.length === 0) {
    return (
      <div className="bus-routes-empty">
        <span className="empty-icon">🚌</span>
        <span>{t('busRoutes.noRoutes')}</span>
      </div>
    );
  }

  const getTimeColor = (minutesStr) => {
    const minutes = parseInt(minutesStr) || 0;
    if (minutes <= 3) return 'time-urgent';
    if (minutes <= 7) return 'time-soon';
    return 'time-later';
  };

  const formatTime = (minutesStr) => {
    const minutes = parseInt(minutesStr) || 0;
    if (minutes === 0) return t('approachingBuses.arriving');
    if (minutes === 1) return `1 ${t('approachingBuses.minute')}`;
    return `${minutes} ${t('approachingBuses.minutes')}`;
  };

  return (
    <div className="bus-routes">
      <div className="routes-header">
        <h3>{t('busRoutes.title')}</h3>
        <div className="routes-header-right">
          {lastUpdated && (
            <span className="last-updated">
              {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <span className="routes-count">{t('busRoutes.total', { count: sortedRoutes.length })}</span>
        </div>
      </div>

      <div className="routes-list">
        {sortedRoutes.map((route) => {
          const arrival = combinedArrivals[route.routeCode];
          const isSearching = searchingRoute === route.routeCode;
          const hasDirectArrival = arrivalsByRoute[route.routeCode];
          const canSearch = !hasDirectArrival && !isSearching;

          return (
            <div
              key={route.routeCode}
              className={`route-card ${arrival && !arrival.notStarted && !arrival.noRouteData && !arrival.stopNotInRoute && !arrival.rateLimited && !arrival.localRateLimited && !arrival.stopsLimited ? 'has-arrival' : ''} ${arrival?.estimated ? 'estimated' : ''} ${arrival?.notStarted || arrival?.noRouteData || arrival?.stopNotInRoute || arrival?.rateLimited || arrival?.localRateLimited || arrival?.stopsLimited ? 'not-started' : ''} ${canSearch ? 'clickable' : ''}`}
              onClick={() => canSearch && handleRouteClick(route.routeCode)}
              title={canSearch ? t('busRoutes.clickToSearch') : ''}
            >
              <div className="route-number">
                <span className="bus-icon">🚌</span>
                <span className="route-code">{route.routeCode}</span>
              </div>
              <div className="route-info">
                <div className="route-name">{route.routeName}</div>
                {isSearching && (
                  <div className="route-searching">
                    <span className="searching-spinner"></span>
                    <span>{t('busRoutes.searching')}</span>
                  </div>
                )}
                {arrival && !arrival.notStarted && (
                  <div className="route-arrival">
                    <span className={`arrival-time ${getTimeColor(arrival.timeInMinutes)}`}>
                      {arrival.estimated ? '~' : ''}{formatTime(arrival.timeInMinutes)}
                    </span>
                    {!arrival.estimated && (
                      <span className="arrival-stops">
                        {arrival.stopsAway} {t('approachingBuses.stops')}
                      </span>
                    )}
                    {arrival.estimated && arrival.foundAtStop && (
                      <span className="found-at-stop" title={t('busRoutes.foundAt', { stop: arrival.foundAtStop })}>
                        📍
                      </span>
                    )}
                  </div>
                )}
                {arrival?.notStarted && (
                  <div className="route-not-started">
                    <span className="not-started-icon">🚏</span>
                    <span>{t('busRoutes.notStarted')}</span>
                  </div>
                )}
                {arrival?.noRouteData && (
                  <div className="route-not-started">
                    <span className="not-started-icon">❓</span>
                    <span>{t('busRoutes.noRouteData')}</span>
                  </div>
                )}
                {arrival?.stopNotInRoute && (
                  <div className="route-not-started">
                    <span className="not-started-icon">⚠️</span>
                    <span>{t('busRoutes.stopNotInRoute')}</span>
                  </div>
                )}
                {arrival?.rateLimited && (
                  <div className="route-not-started">
                    <span className="not-started-icon">⏳</span>
                    <span>{t('busRoutes.rateLimited')}</span>
                  </div>
                )}
                {arrival?.localRateLimited && (
                  <div className="route-not-started">
                    <span className="not-started-icon">🚫</span>
                    <span>{t('busRoutes.localRateLimited')}</span>
                  </div>
                )}
                {arrival?.stopsLimited && (
                  <div className="route-not-started">
                    <span className="not-started-icon">📍</span>
                    <span>{t('busRoutes.stopsLimited')}</span>
                  </div>
                )}
                {!arrival && !isSearching && !hasDirectArrival && (
                  <div className="route-no-data">
                    <span className="search-hint">{t('busRoutes.tapToFind')}</span>
                  </div>
                )}
              </div>
              {arrival && !arrival.notStarted && !arrival.noRouteData && !arrival.stopNotInRoute && !arrival.rateLimited && !arrival.localRateLimited && !arrival.stopsLimited && (
                <div className={`route-time-badge ${getTimeColor(arrival.timeInMinutes)} ${arrival.estimated ? 'estimated' : ''}`}>
                  {arrival.estimated ? '~' : ''}{parseInt(arrival.timeInMinutes) || 0}'
                </div>
              )}
              {(arrival?.notStarted || arrival?.noRouteData || arrival?.stopNotInRoute || arrival?.rateLimited || arrival?.localRateLimited || arrival?.stopsLimited) && (
                <div className="route-time-badge not-started-badge">
                  —
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BusRoutes;
